import { z } from 'zod'
import { pageQuerySchema } from '../pagination'
import type { ReviewAction, ReviewState } from '../vocab'
import type { RecordAction } from './records'

/**
 * Review queue (S14, P-WF-005, GAP-UI-026/027). Lists reviewed records awaiting review or
 * publication, engagements and objectives with unreleased changes (D-06), and meeting minutes
 * when lane E's minutes exist. Drafts and returned records appear only to their contributors.
 */
export const REVIEW_QUEUE_KINDS = [
  'assessments',
  'milestones',
  'dialogues',
  'minutes',
  'engagements',
  'objectives',
] as const
export type ReviewQueueKind = (typeof REVIEW_QUEUE_KINDS)[number]

/** Kind-specific labels (GAP-UI-003, GAP-UI-027). */
export const REVIEW_KIND_LABELS: Record<ReviewQueueKind, { singular: string; plural: string }> = {
  assessments: { singular: 'Assessment', plural: 'Assessments' },
  milestones: { singular: 'Evidence review', plural: 'Evidence reviews' },
  dialogues: { singular: 'Correspondence', plural: 'Correspondence' },
  minutes: { singular: 'Meeting minutes', plural: 'Meeting minutes' },
  engagements: { singular: 'Engagement', plural: 'Engagements' },
  objectives: { singular: 'Objective', plural: 'Objectives' },
}

const ACTION_VERBS: Record<ReviewAction | 'release' | 'correct', string> = {
  submit: 'Submit',
  approve: 'Approve',
  publish: 'Publish',
  return: 'Return',
  retract: 'Retract',
  release: 'Release',
  correct: 'Correct',
}

/** "Approve evidence review", "Publish correspondence", "Release engagement". */
export function reviewActionLabel(
  kind: ReviewQueueKind,
  action: ReviewAction | 'release' | 'correct',
): string {
  const noun = REVIEW_KIND_LABELS[kind].singular
  const object = noun === 'Correspondence' ? 'correspondence' : noun.toLowerCase()
  if (action === 'submit') return `Submit ${object} for review`
  if (action === 'return') return `Return ${object} for changes`
  return `${ACTION_VERBS[action]} ${object}`
}

export const REVIEW_QUEUE_STATES = [
  'draft',
  'returned',
  'in_review',
  'approved',
  'pending_release',
] as const
export type ReviewQueueState = (typeof REVIEW_QUEUE_STATES)[number]

/** `GET /review/queue`. `client_org_id=shared` selects content shared with every subscriber. */
export const reviewQueueQuerySchema = pageQuerySchema.extend({
  kind: z.enum(REVIEW_QUEUE_KINDS).optional(),
  state: z.enum(REVIEW_QUEUE_STATES).optional(),
  client_org_id: z.string().max(64).optional(),
  engagement_id: z.string().max(64).optional(),
})
export type ReviewQueueQuery = z.input<typeof reviewQueueQuerySchema>

/**
 * A person as investors see them: the display name only, never the internal
 * user id (P-ID-006 "Never: internal user IDs").
 */
export interface PersonName {
  name: string
}

/** A person as staff see them: the display name and the user id, for links and filters. */
export interface PersonRef extends PersonName {
  id: string
}

/** A person in a DTO served to both audiences: `PersonRef` for staff, `PersonName` for investors. */
export type PersonView = PersonRef | PersonName

export interface ReviewQueueItem {
  kind: ReviewQueueKind
  id: string
  revision: number
  /** The review state, or `pending_release` for engagements and objectives. */
  state: ReviewState | 'pending_release'
  version: number
  /** e.g. "Assessment v3", "Evidence review", "Engagement". */
  label: string
  /** What the record is about: issuer name, objective or title. */
  title: string
  issuer_id: string | null
  issuer_name: string | null
  engagement_id: string | null
  engagement_title: string | null
  meeting_id: string | null
  client_org_id: string | null
  client_org_name: string | null
  submitted_by: PersonRef | null
  submitted_at: string | null
  updated_at: string
  contributors: PersonRef[]
  /** The caller edited this draft, so another reviewer must approve it (D-07). */
  caller_is_contributor: boolean
  /** Actions the caller may take from the queue (cosmetic, P-UI-003). */
  actions: RecordAction[]
}

export interface ReviewQueueResponse {
  items: ReviewQueueItem[]
  next_cursor: string | null
  /** Items per kind for the current filters other than `kind`. */
  counts: Partial<Record<ReviewQueueKind, number>>
}
